import type { ProjectStructureView } from '@obraplan/core/application/use-cases/structure/get-project-structure';
import type { ProjectTakeoff } from '@obraplan/core/application/use-cases/takeoff/takeoff-use-cases';
import type { ProjectGeometry } from '@obraplan/core/application/use-cases/takeoff/geometry-use-cases';
import type { ProjectServiceItems } from '@obraplan/core/application/use-cases/takeoff/service-items-use-cases';
import type { SaveProjectServiceItemsRequest } from '@obraplan/core/application/contracts/takeoff';
import type { Service, ServiceItem } from '@obraplan/core/domain/takeoff/service';
import type { MeasureBy } from '@obraplan/core/domain/takeoff/geometry';
import type { AppliesTo } from '@obraplan/core/domain/takeoff/applies-to';
import { normalizeName } from '@obraplan/core/domain/shared/naming';
import { itemQuantityKey } from '@obraplan/core/domain/takeoff/consolidation';
import type { ServiceQuantityDecision } from '@obraplan/core/domain/takeoff/service-quantity';
import type { QuantidadesPorPeca } from '@obraplan/core/domain/takeoff/planilha-por-peca';
import { pieceAliasMap } from '@obraplan/core/domain/takeoff/imported-piece';
import {
  serviceKey,
  type CatalogDiscipline,
  type CatalogItem,
  type CatalogService,
  type ServiceCatalog,
} from '@obraplan/core/domain/takeoff/service-catalog';
import type { SteelRate } from '@obraplan/core/domain/takeoff/steel';
import type { Room } from '@obraplan/core/domain/takeoff/room';
import type { Opening, Wall, WallOwner } from '@obraplan/core/domain/takeoff/wall';
import type {
  ElementSegment,
  StructuralElement,
} from '@obraplan/core/domain/takeoff/structural-element';
import { alwaysHasUnits, type FloorPlanKind } from '@obraplan/core/domain/takeoff/floor-plan';
import type {
  DisciplineId,
  JobRoleId,
  ProjectId,
  ServiceId,
  UnitTypeId,
} from '@obraplan/core/domain/shared/ids';
import {
  emptyDraft,
  lembrarOsLados,
  type FloorDraft,
  type TowerDraft,
  type TypologyDraft,
  type WallLabel,
} from './typology-draft';
import type { WorkspaceDraft } from './typology-draft';

/**
 * O rascunho encontra o banco.
 *
 * A partir da fatia 1 da Fase 14, a **identidade** do quantitativo é do servidor — o
 * desenho do pavimento, a tipologia e a posição são registros da obra. O que continua no
 * navegador é a **geometria**: ambientes, paredes, vãos e o que foi lançado neles, que
 * ainda não tem tabela.
 *
 * Esta função junta os dois numa `WorkspaceDraft` com a forma que as telas já leem. É o
 * que permitiu ligar a espinha ao banco sem reescrever a montagem inteira: `floorsOf`,
 * `unitsOf`, `typologiesOfFloor`, a consolidação e os cartões de geometria continuam
 * recebendo exatamente o que recebiam, e não sabem de onde veio.
 *
 * A regra de precedência é uma só e vale para tudo: **o servidor manda na identidade, o
 * rascunho manda na geometria.** Tipologia que sumiu do servidor sai da tela, ainda que o
 * rascunho tenha a geometria dela guardada — o contrário ressuscitaria, na mesa de quem
 * abriu a tela, uma tipologia que outra pessoa removeu.
 */

/**
 * Só pavimento **com desenho ligado** entra.
 *
 * É a definição de estar no quantitativo: o pavimento existe na obra desde a Fase 2, com
 * atividade e prazo, e entra aqui quando alguém diz qual desenho ele usa. Sem o vínculo
 * não há tipo de pavimento a atribuir, e inventar um faria a tela contar andares que
 * ninguém colocou na conta.
 */
function pavimentosLigados(
  estrutura: ProjectStructureView,
  kindPorDesenho: ReadonlyMap<string, FloorPlanKind>,
): TowerDraft[] {
  return estrutura.tree.towers
    .map((torre): TowerDraft => {
      const floors = torre.floors.flatMap((node): FloorDraft[] => {
        const desenho = node.floor.floorPlanId;
        if (desenho === undefined) return [];
        const kind = kindPorDesenho.get(desenho);
        if (kind === undefined) return [];

        return [
          {
            id: node.floor.id,
            name: node.floor.name,
            level: node.floor.level,
            kind,
            floorPlanId: desenho,
            // Deixou de ser marcação manual: quem sabe se o pavimento tem apto é o
            // desenho dele, e a função que responde isso já existia no domínio.
            hasUnits: alwaysHasUnits(kind),
          },
        ];
      });
      return { id: torre.tower.id, name: torre.tower.name, floors };
    })
    .filter((torre) => torre.floors.length > 0);
}

/**
 * Em que pavimento cada tipologia da obra mora, pela posição dela. É o mesmo que a montagem
 * mostra, e o rascunho precisa dele também: as peças da Fundação se conferem lá
 * (`estacaCabeAqui`), e com o `TIPO` de quem nasce a estaca e o bloco eram recusados na área
 * comum da Fundação que a tela mostrava.
 */
export function pavimentosDaObra(
  quantitativo: ProjectTakeoff,
): ReadonlyMap<UnitTypeId, FloorPlanKind> {
  return pavimentoPorTipologia(
    quantitativo,
    new Map(quantitativo.floorPlans.map((plano) => [plano.id, plano.kind])),
  );
}

/** Em que pavimento cada tipologia mora — pela posição dela, e `TIPO` quando não tem. */
function pavimentoPorTipologia(
  quantitativo: ProjectTakeoff,
  kindPorDesenho: ReadonlyMap<string, FloorPlanKind>,
): ReadonlyMap<UnitTypeId, FloorPlanKind> {
  const porTipologia = new Map<UnitTypeId, FloorPlanKind>();
  for (const posicao of quantitativo.positions) {
    const kind = kindPorDesenho.get(posicao.floorPlanId);
    if (kind !== undefined && !porTipologia.has(posicao.unitTypeId)) {
      porTipologia.set(posicao.unitTypeId, kind);
    }
  }
  return porTipologia;
}

/**
 * Quantas cabem em cada pavimento — o `count` da posição.
 *
 * Sai daqui pelo mesmo motivo que o tipo de pavimento sai: é identidade, e identidade é
 * do servidor. Uma para o apto e para a área comum; quarenta para a vaga da garagem.
 */
function quantidadePorTipologia(quantitativo: ProjectTakeoff): ReadonlyMap<UnitTypeId, number> {
  const porTipologia = new Map<UnitTypeId, number>();
  for (const posicao of quantitativo.positions) {
    if (!porTipologia.has(posicao.unitTypeId)) porTipologia.set(posicao.unitTypeId, posicao.count);
  }
  return porTipologia;
}

export function montarWorkspace(
  rascunho: WorkspaceDraft,
  estrutura: ProjectStructureView,
  quantitativo: ProjectTakeoff,
): WorkspaceDraft {
  const kindPorDesenho = new Map<string, FloorPlanKind>(
    quantitativo.floorPlans.map((plano) => [plano.id, plano.kind]),
  );
  const floorKinds = pavimentoPorTipologia(quantitativo, kindPorDesenho);
  // O desenho de cada tipologia, pela primeira posição dela — é o que separa dois desenhos do
  // mesmo tipo na bancada (`sameDrawing`).
  const desenhoPorTipologia = new Map<UnitTypeId, string>();
  for (const posicao of quantitativo.positions) {
    if (!desenhoPorTipologia.has(posicao.unitTypeId)) {
      desenhoPorTipologia.set(posicao.unitTypeId, posicao.floorPlanId);
    }
  }
  const quantidades = quantidadePorTipologia(quantitativo);
  const geometriaGuardada = new Map(
    rascunho.typologies.map((typology) => [typology.unitTypeId, typology]),
  );

  const typologies = quantitativo.unitTypes.map((unitType): TypologyDraft => {
    // A geometria é do rascunho; quando não há nenhuma guardada, uma em branco com a
    // identidade certa. `emptyDraft` gera um id próprio, que é descartado aqui — o id
    // que vale é o do servidor.
    // O desenho guardado no navegador não vale: quem diz o desenho é a posição no servidor.
    const { floorPlanId: _doRascunho, ...guardada } =
      geometriaGuardada.get(unitType.id) ?? emptyDraft(unitType.name, unitType.kind);
    return {
      ...guardada,
      unitTypeId: unitType.id,
      name: unitType.name,
      kind: unitType.kind,
      floorKind: floorKinds.get(unitType.id) ?? 'TIPO',
      ...(desenhoPorTipologia.has(unitType.id)
        ? { floorPlanId: desenhoPorTipologia.get(unitType.id)! }
        : {}),
      unitsPerFloor: quantidades.get(unitType.id) ?? 1,
    };
  });

  // Bancada nunca fica sem tipologia na mesa: sem nenhuma no servidor, uma em branco
  // segura a tela, e ela não é gravada em lugar nenhum.
  const emBranco = emptyDraft();
  const naMesa = typologies.some((typology) => typology.unitTypeId === rascunho.activeId);

  return {
    ...rascunho,
    towers: pavimentosLigados(estrutura, kindPorDesenho),
    typologies: typologies.length > 0 ? typologies : [emBranco],
    activeId: naMesa ? rascunho.activeId : (typologies[0]?.unitTypeId ?? emBranco.unitTypeId),
  };
}

/**
 * O que volta para o `localStorage`: só a geometria.
 *
 * Guardar torres e tipologias de novo criaria uma segunda cópia da identidade, e ela
 * envelheceria — alguém renomeia a tipologia noutra máquina e a mesa continuaria
 * chamando-a pelo nome antigo até o rascunho ser descartado.
 */
export function apenasGeometria(workspace: WorkspaceDraft): WorkspaceDraft {
  return { ...workspace, towers: [] };
}

// ── O desenho, indo e voltando ──────────────────────────────────────────────────

/**
 * A geometria do servidor é **plana**; a bancada é por tipologia.
 *
 * O servidor guarda cinco listas da obra inteira — é o que as tabelas são, e é o que
 * permite ler o desenho numa consulta por tipo de peça. A bancada precisa da mesma coisa
 * repartida: cada tipologia com os ambientes dela, e as **divisas** de lado, na bancada,
 * porque elas são do pavimento e não de nenhuma das duas (RN-163).
 *
 * As duas funções abaixo são a dobradiça, e são uma o inverso da outra. O que entra por
 * `geometriaParaRascunho` sai igual por `geometriaDoRascunho` — e é essa ida e volta que
 * o teste exercita, porque uma peça que se perde no caminho não quebra nada: ela some do
 * total, e o total continua parecendo um total.
 */

/** O dono da peça decide onde ela mora na bancada: tipologia, ou divisa do pavimento. */
function daTipologia(owner: WallOwner, unitTypeId: UnitTypeId): boolean {
  return owner.kind === 'UNIT_TYPE' && owner.unitTypeId === unitTypeId;
}

/**
 * Os rótulos das paredes, refeitos do desenho.
 *
 * O nome automático — "Sala · Lado 1 ↔ Quarto 1 · Lado 3" — **não** é gravado: ele se
 * deduz de quem a parede separa e da ordem em que ela apareceu no ambiente. Guardá-lo
 * seria guardar uma cópia do nome do ambiente para envelhecer à parte dele: renomear a
 * Sala deixaria doze paredes chamando-a pelo nome antigo.
 *
 * O que **é** gravado é o nome que alguém digitou (`wall.label`), porque esse ninguém
 * consegue deduzir.
 *
 * A numeração pode não devolver exatamente a que existia antes de duas paredes casarem —
 * a que foi descartada no casamento levou o número dela junto. É rótulo de tela, e o que
 * importa dele é dizer qual parede é qual.
 */
function rotulosDe(rooms: readonly Room[], walls: readonly Wall[]): Record<string, WallLabel> {
  const nomeDo = new Map(rooms.map((room) => [String(room.id), room.name]));
  const ordem = new Map<string, number>();
  const ladoDe = new Map<string, string>();

  for (const wall of walls) {
    for (const lado of wall.sideB === undefined ? [wall.sideA] : [wall.sideA, wall.sideB]) {
      const quarto = String(lado.roomId);
      const nome = nomeDo.get(quarto);
      if (nome === undefined) continue;
      const indice = (ordem.get(quarto) ?? 0) + 1;
      ordem.set(quarto, indice);
      ladoDe.set(`${String(wall.id)}|${quarto}`, `${nome} · Lado ${String(indice)}`);
    }
  }

  const rotulos: Record<string, WallLabel> = {};
  for (const wall of walls) {
    const a = ladoDe.get(`${String(wall.id)}|${String(wall.sideA.roomId)}`);
    if (a === undefined) continue;
    const b =
      wall.sideB === undefined
        ? undefined
        : ladoDe.get(`${String(wall.id)}|${String(wall.sideB.roomId)}`);
    rotulos[wall.id] = {
      a,
      ...(b === undefined ? {} : { b }),
      ...(wall.label === undefined ? {} : { custom: wall.label }),
    };
  }
  return rotulos;
}

/**
 * Os ambientes na ordem em que foram traçados — a do rascunho, e não a do servidor.
 *
 * A ordem não é enfeite: a divisa é desenhada sob o **primeiro** ambiente da lista que a tem
 * (`placeWalls`), e o vão e a peça guardam a posição a partir do começo dela e para a
 * esquerda dela. O rascunho guarda os ambientes na ordem do traço; o servidor os devolve pelo
 * nome. Com a sala traçada antes do quarto, na volta do servidor o quarto vinha primeiro, a
 * divisa passava a ser desenhada de trás para frente, e o pilar encostado nela saltava para o
 * espelho — "os pilares se movem", 02/10/2026.
 *
 * Quem o rascunho já conhece fica na posição dele; quem ele não conhece (a bancada aberta
 * num navegador novo) vai pela data de criação, que é a ordem do traço, e pelo id no empate.
 */
function naOrdemEmQueForamTracados(
  doServidor: readonly Room[],
  doRascunho: readonly Room[],
): Room[] {
  const posicao = new Map(doRascunho.map((room, indice) => [String(room.id), indice]));
  const conhecido = (room: Room): number => posicao.get(String(room.id)) ?? Infinity;
  return [...doServidor].sort(
    (a, b) =>
      conhecido(a) - conhecido(b) ||
      (conhecido(a) === Infinity
        ? a.createdAt.localeCompare(b.createdAt) || String(a.id).localeCompare(String(b.id))
        : 0),
  );
}

/** O desenho do servidor entrando na bancada — cada peça no lugar em que a tela a procura. */
export function geometriaParaRascunho(
  rascunho: WorkspaceDraft,
  geometria: ProjectGeometry,
): WorkspaceDraft {
  const vaosDe = (walls: readonly Wall[]): Opening[] => {
    const daqui = new Set(walls.map((wall) => String(wall.id)));
    return geometria.openings.filter((opening) => daqui.has(String(opening.wallId)));
  };
  const trechosDe = (elements: readonly StructuralElement[]): ElementSegment[] => {
    const daqui = new Set(elements.map((element) => String(element.id)));
    return geometria.segments.filter((segment) => daqui.has(String(segment.elementId)));
  };

  const divisas = geometria.walls.filter((wall) => wall.owner.kind === 'FLOOR_PLAN');
  const estruturaDaDivisa = geometria.elements.filter(
    (element) => element.owner.kind === 'FLOOR_PLAN',
  );

  const typologies = rascunho.typologies.map((typology): TypologyDraft => {
    const rooms = naOrdemEmQueForamTracados(
      geometria.rooms.filter((room) => room.unitTypeId === typology.unitTypeId),
      typology.rooms,
    );
    const walls = geometria.walls.filter((wall) => daTipologia(wall.owner, typology.unitTypeId));
    const elements = geometria.elements.filter((element) =>
      daTipologia(element.owner, typology.unitTypeId),
    );
    // O shaft mora com o ambiente dele, e o ambiente diz a tipologia (ADR-150).
    const ambientes = new Set(rooms.map((room) => String(room.id)));
    // A estaca mora na tipologia que ela aponta (RN-361), e a marca com a estaca (Fase 27).
    const pecas = geometria.pieces?.filter((piece) => piece.unitTypeId === typology.unitTypeId);
    const dasPecas = new Set((pecas ?? typology.pieces ?? []).map((piece) => String(piece.id)));

    return {
      ...typology,
      rooms,
      walls,
      openings: vaosDe(walls),
      elements,
      elementSegments: trechosDe(elements),
      ...(geometria.shafts === undefined
        ? {}
        : {
            shafts: geometria.shafts.filter((shaft) => ambientes.has(String(shaft.roomId))),
          }),
      // Ausente é o servidor que não conhece a estaca: a tela fica com as que tinha.
      ...(pecas === undefined ? {} : { pieces: pecas }),
      ...(geometria.pieceMarks === undefined
        ? {}
        : {
            pieceMarks: geometria.pieceMarks.filter((mark) => dasPecas.has(String(mark.pieceId))),
          }),
      // O bloco e o baldrame moram na tipologia que apontam, como a estaca (etapa 2).
      // Ausente é o servidor de antes deles: a tela fica com os que tinha.
      ...(geometria.pileCaps === undefined
        ? {}
        : {
            pileCaps: geometria.pileCaps.filter((cap) => cap.unitTypeId === typology.unitTypeId),
          }),
      ...(geometria.groundBeams === undefined
        ? {}
        : {
            groundBeams: geometria.groundBeams.filter(
              (beam) => beam.unitTypeId === typology.unitTypeId,
            ),
          }),
      // O radier e a laje também (etapa 3): moram na área comum que apontam (F3).
      ...(geometria.slabs === undefined
        ? {}
        : { slabs: geometria.slabs.filter((slab) => slab.unitTypeId === typology.unitTypeId) }),
      // O muro e o pilar de contenção (etapa 4), na área comum que apontam (RN-361).
      ...(geometria.retainingWalls === undefined
        ? {}
        : {
            retainingWalls: geometria.retainingWalls.filter(
              (wall) => wall.unitTypeId === typology.unitTypeId,
            ),
          }),
      ...(geometria.retainingColumns === undefined
        ? {}
        : {
            retainingColumns: geometria.retainingColumns.filter(
              (column) => column.unitTypeId === typology.unitTypeId,
            ),
          }),
      // A escada (etapa 5), na área comum que aponta (RN-361).
      ...(geometria.stairs === undefined
        ? {}
        : {
            stairs: geometria.stairs.filter((stair) => stair.unitTypeId === typology.unitTypeId),
          }),
      // O rótulo enxerga as divisas também: a parede que encosta nesta tipologia tem nome
      // aqui, ainda que more na bancada.
      labels: rotulosDe(rooms, [...walls, ...divisas]),
    };
  });

  // A parede gravada antes de lembrar o lado do contorno ganha o lado aqui, na chegada; a
  // gravação seguinte o leva ao banco, e o desenho deixa de procurá-la pela medida (RN-244).
  return lembrarOsLados({
    ...rascunho,
    // Os tipos de estaca são da obra, e moram na bancada (Fase 27, D1).
    ...(geometria.pileTypes === undefined ? {} : { pileTypes: geometria.pileTypes }),
    // Os de bloco também (Fase 41, RN-536). Ausente é o servidor de antes deles: a tela fica
    // com os que tinha.
    ...(geometria.pileCapTypes === undefined ? {} : { pileCapTypes: geometria.pileCapTypes }),
    // Os de pilar e de viga também (Fase 42, RN-561); o tipo, o fator e a taxa de aço de cada
    // peça vêm dentro dela (`elementTypeId`, `factors`, `steelRates`), em `elements`, que entra
    // em cada tipologia e na divisa como veio. Ausente é o servidor de antes deles: a tela fica
    // com os que tinha.
    ...(geometria.elementTypes === undefined ? {} : { elementTypes: geometria.elementTypes }),
    typologies,
    partyWalls: divisas,
    partyOpenings: vaosDe(divisas),
    partyElements: estruturaDaDivisa,
    partyElementSegments: trechosDe(estruturaDaDivisa),
    partyLabels: rotulosDe(geometria.rooms, divisas),
  });
}

/** A bancada virando o envelope que o servidor grava — o inverso do de cima. */
export function geometriaDoRascunho(workspace: WorkspaceDraft): ProjectGeometry {
  // O nome digitado volta para a parede: na bancada ele mora no rótulo, e é o rótulo que
  // a tela edita. Sem esta costura, renomear uma parede duraria até a próxima recarga.
  const comONome = (wall: Wall, rotulos: Readonly<Record<string, WallLabel>>): Wall => {
    const custom = rotulos[wall.id]?.custom;
    return custom === undefined ? semNome(wall) : { ...wall, label: custom };
  };

  const rooms = workspace.typologies.flatMap((typology) => typology.rooms);
  const walls = [
    ...workspace.typologies.flatMap((typology) =>
      typology.walls.map((wall) => comONome(wall, typology.labels)),
    ),
    ...(workspace.partyWalls ?? []).map((wall) => comONome(wall, workspace.partyLabels ?? {})),
  ];

  return {
    rooms,
    walls,
    openings: [
      ...workspace.typologies.flatMap((typology) => typology.openings),
      ...(workspace.partyOpenings ?? []),
    ],
    elements: [
      ...workspace.typologies.flatMap((typology) => typology.elements),
      ...(workspace.partyElements ?? []),
    ],
    segments: [
      ...workspace.typologies.flatMap((typology) => typology.elementSegments),
      ...(workspace.partyElementSegments ?? []),
    ],
    // Sempre presente, ainda que vazia: esta bancada conhece o shaft, e a lista vazia é
    // "nenhum" — o que a tela de antes, que não o conhecia, não podia dizer (RN-272).
    shafts: workspace.typologies.flatMap((typology) => typology.shafts ?? []),
    // A fundação na planta (Fase 27, etapa 1b). A estaca e a marca sobem sempre, como o
    // shaft: esta bancada as conhece, e a lista vazia é "nenhuma". O tipo sobe só quando a
    // bancada já tem a lista da obra — antes de ela chegar, ausente é "não sei", e mandar a
    // vazia excluiria os tipos de quem os cadastrou.
    ...(workspace.pileTypes === undefined ? {} : { pileTypes: workspace.pileTypes }),
    pieces: workspace.typologies.flatMap((typology) => typology.pieces ?? []),
    pieceMarks: workspace.typologies.flatMap((typology) => typology.pieceMarks ?? []),
    // Os tipos de bloco (Fase 41, RN-536) sobem como os de estaca: só quando a bancada já tem a
    // lista da obra. O tipo e o fator de cada bloco, e o fator do baldrame e do radier, vão
    // dentro da peça (`pileCapTypeId`, `factors`), como o fator da estaca. A taxa de aço da
    // etapa 2 (RN-543, RN-544) também: `steelRates` no tipo e em cada peça.
    ...(workspace.pileCapTypes === undefined ? {} : { pileCapTypes: workspace.pileCapTypes }),
    // Os tipos de pilar e de viga (Fase 42, RN-561), pela mesma regra: só quando a bancada já tem
    // a lista da obra. O tipo, o fator próprio e a taxa de aço própria de cada pilar e viga sobem
    // dentro da peça (`elementTypeId`, `factors`, `steelRates`), em `elements`; a do tipo, no
    // tipo (etapa 2 — RN-567, RN-568).
    ...(workspace.elementTypes === undefined ? {} : { elementTypes: workspace.elementTypes }),
    // O bloco e o baldrame (etapa 2) sobem sempre, pela mesma razão da estaca.
    pileCaps: workspace.typologies.flatMap((typology) => typology.pileCaps ?? []),
    groundBeams: workspace.typologies.flatMap((typology) => typology.groundBeams ?? []),
    // O radier e a laje (etapa 3), pela mesma razão.
    slabs: workspace.typologies.flatMap((typology) => typology.slabs ?? []),
    // O muro e o pilar de contenção (etapa 4), pela mesma razão. O fator e a taxa de aço próprios
    // de cada um (Fase 42, etapa 3 — RN-571, RN-572) vão dentro da peça (`factors`,
    // `steelRates`), como no bloco: ausente fica o gravado, vazio apaga.
    retainingWalls: workspace.typologies.flatMap((typology) => typology.retainingWalls ?? []),
    retainingColumns: workspace.typologies.flatMap((typology) => typology.retainingColumns ?? []),
    // A escada (etapa 5), pela mesma razão — e com o fator e a taxa dentro dela também.
    stairs: workspace.typologies.flatMap((typology) => typology.stairs ?? []),
  };
}

/** Tira o nome da parede que deixou de ter um — `undefined` não apaga coluna, ausência sim. */
function semNome(wall: Wall): Wall {
  if (wall.label === undefined) return wall;
  const { label: _fora, ...resto } = wall;
  return resto;
}

/**
 * Do desenho da mesa, só o que é comprovadamente da obra do `quantitativo` — e o mesmo objeto
 * quando tudo é (30/10/2026).
 *
 * O rascunho do navegador não diz de que obra é: depois de trocar de obra, e na recarga, a mesa
 * ainda é a da última aberta. As tipologias o `SYNC_TYPOLOGIES` troca pelas da obra; a divisa, o
 * pilar e a viga dela e os tipos de estaca, de bloco e de pilar e viga moram na bancada e
 * ficavam — e, com o desenho da obra nova vazio no servidor, subiam para ela como se fossem a
 * migração (a divisa de A gravada em B, com o pavimento de A). A prova de dono é o que cada peça
 * aponta: uma tipologia ou um pavimento da obra, ou a própria obra, no tipo. O vão, o trecho, o
 * shaft e a marca vão com a parede, o pilar, o ambiente e a estaca deles.
 */
export function desenhoDaObra(
  geometria: ProjectGeometry,
  quantitativo: ProjectTakeoff,
): ProjectGeometry {
  const tipologias = new Set(quantitativo.unitTypes.map((unitType) => String(unitType.id)));
  const pavimentos = new Set(quantitativo.floorPlans.map((plano) => String(plano.id)));
  const obras = new Set(
    [...quantitativo.unitTypes, ...quantitativo.floorPlans].map((dela) => String(dela.projectId)),
  );
  const doDono = (owner: WallOwner): boolean =>
    owner.kind === 'UNIT_TYPE'
      ? tipologias.has(String(owner.unitTypeId))
      : pavimentos.has(String(owner.floorPlanId));
  const naTipologia = (peca: { readonly unitTypeId: UnitTypeId }): boolean =>
    tipologias.has(String(peca.unitTypeId));
  const daObra = (tipo: { readonly projectId: ProjectId }): boolean =>
    obras.has(String(tipo.projectId));

  const rooms = geometria.rooms.filter(naTipologia);
  const walls = geometria.walls.filter((wall) => doDono(wall.owner));
  const elements = geometria.elements.filter((element) => doDono(element.owner));
  const pieces = geometria.pieces?.filter(naTipologia);
  const ambientes = new Set(rooms.map((room) => String(room.id)));
  const paredes = new Set(walls.map((wall) => String(wall.id)));
  const pilares = new Set(elements.map((element) => String(element.id)));
  const estacas = new Set((pieces ?? []).map((piece) => String(piece.id)));

  const filtrado: ProjectGeometry = {
    ...geometria,
    rooms,
    walls,
    openings: geometria.openings.filter((opening) => paredes.has(String(opening.wallId))),
    elements,
    segments: geometria.segments.filter((segment) => pilares.has(String(segment.elementId))),
    ...(geometria.shafts === undefined
      ? {}
      : { shafts: geometria.shafts.filter((shaft) => ambientes.has(String(shaft.roomId))) }),
    ...(pieces === undefined ? {} : { pieces }),
    ...(geometria.pieceMarks === undefined
      ? {}
      : { pieceMarks: geometria.pieceMarks.filter((mark) => estacas.has(String(mark.pieceId))) }),
    ...(geometria.pileTypes === undefined ? {} : { pileTypes: geometria.pileTypes.filter(daObra) }),
    ...(geometria.pileCapTypes === undefined
      ? {}
      : { pileCapTypes: geometria.pileCapTypes.filter(daObra) }),
    ...(geometria.elementTypes === undefined
      ? {}
      : { elementTypes: geometria.elementTypes.filter(daObra) }),
    ...(geometria.pileCaps === undefined
      ? {}
      : { pileCaps: geometria.pileCaps.filter(naTipologia) }),
    ...(geometria.groundBeams === undefined
      ? {}
      : { groundBeams: geometria.groundBeams.filter(naTipologia) }),
    ...(geometria.slabs === undefined ? {} : { slabs: geometria.slabs.filter(naTipologia) }),
    ...(geometria.retainingWalls === undefined
      ? {}
      : { retainingWalls: geometria.retainingWalls.filter(naTipologia) }),
    ...(geometria.retainingColumns === undefined
      ? {}
      : { retainingColumns: geometria.retainingColumns.filter(naTipologia) }),
    ...(geometria.stairs === undefined ? {} : { stairs: geometria.stairs.filter(naTipologia) }),
  };
  return quantasPecas(filtrado) === quantasPecas(geometria) ? geometria : filtrado;
}

/** Quantas peças o desenho tem, somadas todas as listas — o que `desenhoDaObra` tirou, se tirou. */
function quantasPecas(geometria: ProjectGeometry): number {
  return Object.values(geometria).reduce<number>(
    (total, lista) => total + (Array.isArray(lista) ? lista.length : 0),
    0,
  );
}

// ── O cadastro de serviços, indo e voltando ─────────────────────────────────────

/**
 * O catálogo tem duas metades, e só uma delas tem tabela (Fase 14, fatia 4).
 *
 * **Do servidor** vêm a disciplina, o serviço, a medida e a espessura — as quarenta
 * decisões de uma tarde, que até agora morriam com o `localStorage`.
 *
 * **Do rascunho** continuavam os itens e a quantidade que cada aba da planilha declarou,
 * porque o item apontava um `Material` e 140 dos 218 itens do cliente não têm código. Desde
 * a fatia 3 da Fase 24 eles também são do servidor — ver "Os itens do serviço, indo e
 * voltando", abaixo —, e o merge daqui ficou só para a medida e a espessura.
 *
 * Então a junção é um **merge**, e não uma substituição: o servidor manda no que é dele, o
 * rascunho guarda o que ainda não tem onde morar, e a costura é a chave natural
 * `(disciplina, serviço)` — a mesma da RN-164, porque a planilha não tem id nenhum.
 */

/** O envelope que `ApplyServiceCatalog` recebe — só a metade que tem tabela. */
export function catalogoParaServidor(
  catalog: ServiceCatalog,
): { readonly name: string; readonly services: readonly CatalogServiceInput[] }[] {
  return catalog.disciplines.map((disciplina) => ({
    name: disciplina.name,
    services: disciplina.services.map((servico) => ({
      name: servico.name,
      measureBy: servico.measureBy,
      // Pendente vai sem elemento, e o servidor mantém o que tinha (RN-256).
      ...(servico.appliesTo === undefined ? {} : { appliesTo: servico.appliesTo }),
      ...(servico.layerThickness === undefined ? {} : { layerThickness: servico.layerThickness }),
      ...(servico.sideMargin === undefined ? {} : { sideMargin: servico.sideMargin }),
    })),
  }));
}

interface CatalogServiceInput {
  readonly name: string;
  readonly measureBy: MeasureBy;
  readonly appliesTo?: AppliesTo;
  readonly layerThickness?: number;
  /** A margem da soleira e do peitoril, em metros para cada lado (RN-308). */
  readonly sideMargin?: number;
}

/**
 * O cadastro do servidor entra no catálogo do rascunho.
 *
 * Para o serviço que os dois conhecem, o servidor manda na medida, na espessura e na margem
 * da soleira (RN-308). Para o que só o servidor tem — o caso de quem limpou o navegador —, ele
 * entra **sem itens**, e é
 * por isso que a tela precisa dizer que o item ainda é local: uma lista de serviços sem
 * item nenhum parece um catálogo vazio, e não é.
 *
 * `measureOrigin` vira `DECIDIDO` no que veio gravado, e não é detalhe: o que está no banco
 * alguém confirmou na tela, e reimportar a planilha não pode reescrever isso por cima.
 */
export function catalogoDoServidor(
  catalog: ServiceCatalog,
  services: readonly Service[],
  nomeDaDisciplina: (id: DisciplineId) => string | undefined,
): ServiceCatalog {
  const porChave = new Map<string, Service>();
  const porDisciplina = new Map<string, Service[]>();
  for (const service of services) {
    const disciplina = nomeDaDisciplina(service.disciplineId);
    if (disciplina === undefined) continue;
    porChave.set(serviceKey(disciplina, service.name), service);
    const lista = porDisciplina.get(disciplina);
    if (lista === undefined) porDisciplina.set(disciplina, [service]);
    else lista.push(service);
  }

  const disciplines = catalog.disciplines.map((disciplina) => ({
    ...disciplina,
    services: disciplina.services.map((servico) => {
      const gravado = porChave.get(serviceKey(disciplina.name, servico.name));
      if (gravado === undefined) return servico;
      const {
        layerThickness: _antiga,
        sideMargin: _margem,
        appliesTo: _elemento,
        ...semEspessura
      } = servico;
      return {
        ...semEspessura,
        measureBy: gravado.measureBy,
        // O elemento vem com a medida: é um par, e o servidor manda nos dois (RN-256).
        ...(gravado.appliesTo === undefined ? {} : { appliesTo: gravado.appliesTo }),
        measureOrigin: 'DECIDIDO' as const,
        ...(gravado.layerThickness === undefined ? {} : { layerThickness: gravado.layerThickness }),
        ...(gravado.sideMargin === undefined ? {} : { sideMargin: gravado.sideMargin }),
      };
    }),
  }));

  // O que o servidor tem e o rascunho não: disciplina nova, ou serviço que o navegador
  // perdeu. Entra sem item, porque item não subiu.
  const conhecidas = new Set(disciplines.map((disciplina) => normalizeName(disciplina.name)));
  const novas = [...porDisciplina.entries()]
    .filter(([nome]) => !conhecidas.has(normalizeName(nome)))
    .map(([nome, doServidor]) => ({
      key: normalizeName(nome),
      name: nome,
      services: doServidor.map((service) => paraCatalogo(service, nome)),
    }));

  const comOsQueFaltavam = disciplines.map((disciplina) => {
    const doServidor = porDisciplina.get(disciplina.name) ?? [];
    const jaTem = new Set(
      disciplina.services.map((servico) => serviceKey(disciplina.name, servico.name)),
    );
    const faltando = doServidor
      .filter((service) => !jaTem.has(serviceKey(disciplina.name, service.name)))
      .map((service) => paraCatalogo(service, disciplina.name));
    return faltando.length === 0
      ? disciplina
      : { ...disciplina, services: [...disciplina.services, ...faltando] };
  });

  return { ...catalog, disciplines: [...comOsQueFaltavam, ...novas] };
}

// ── Os itens do serviço, indo e voltando (Fase 24, fatia 3) ─────────────────────

/**
 * A outra metade do catálogo também tem tabela agora (RN-249): o item de cada serviço, a
 * quantidade que cada aba declarou, a planilha de onde veio, a aba que orça cada tipologia
 * e o número que alguém digitou por cima dela. O navegador deixa de ser dono de tudo isso.
 *
 * As funções abaixo são a dobradiça, como as da geometria: `itensParaServidor` monta o
 * envelope que `SaveProjectServiceItems` recebe, e `itensDoServidor` com
 * `lancamentosDoServidor` põem de volta na bancada o que `GetProjectServiceItems` leu. O
 * que entra por uma sai igual pela outra — é essa ida e volta que o teste exercita, porque
 * a bancada compara o envelope com o último que subiu para decidir se grava de novo, e uma
 * volta que perdesse um campo faria a tela regravar a obra a cada abertura.
 */

/** O envelope que `SaveProjectServiceItems` recebe — sem a obra, que é de quem grava. */
export type ItensParaGravar = Omit<SaveProjectServiceItemsRequest, 'projectId'>;

/** O serviço não tem nada desta metade: nem item, nem aba, nem unidade da planilha. */
function servicoVazio(servico: CatalogService): boolean {
  return (
    servico.items.length === 0 &&
    Object.keys(servico.quantityBySheet).length === 0 &&
    servico.unit === undefined
  );
}

/**
 * A bancada virando o envelope dos itens.
 *
 * **É o retrato inteiro** (RN-255): o servidor tira o item que não veio. Por isso o serviço
 * vazio fica de fora sem perda nenhuma — ausente e vazio dizem a mesma coisa —, e o
 * catálogo removido (`CLEAR_CATALOG`) vira envelope sem nada, que é o que "remover a
 * planilha" quer dizer.
 *
 * `tipologias` é o escopo da quantidade digitada: só as da obra, porque a bancada sem
 * tipologia nenhuma segura na mesa uma de rascunho, com id que não existe em lugar nenhum.
 */
export function itensParaServidor(
  workspace: WorkspaceDraft,
  tipologias: ReadonlySet<string>,
): ItensParaGravar {
  const catalogo = workspace.catalog;
  const daObra = workspace.typologies.filter((typology) => tipologias.has(typology.unitTypeId));
  /**
   * As tipologias cujas peças importadas este envelope descreve (Fase 28, RN-390): as que têm
   * o campo `pieceQuantities` — com peça, ou esvaziado nesta mesa.
   *
   * A regra é a do campo, e não "todas as da obra", por causa do que o servidor faz com a
   * lista: a tipologia listada tem as peças **trocadas** pelas do envelope. Listar a que esta
   * mesa nunca carregou com peça apagaria o que outra aba acabou de importar nela. Com o
   * campo, a lista é exatamente: as que o servidor devolveu com peça (`HYDRATE_ITEMS`), as que
   * receberam a planilha aqui (`IMPORT_PIECE_QUANTITIES`) e as que perderam a última peça aqui
   * (o serviço, o item ou o catálogo removido deixam o campo vazio, e não ausente) — é assim
   * que apagar chega ao banco.
   */
  const comPecas = daObra.filter((typology) => typology.pieceQuantities !== undefined);

  /**
   * A quantidade de cada peça, já no formato do envelope, de uma chave da tela — a do serviço
   * ou a do item. Nenhuma peça é campo ausente: o envelope de quem não importou nada sai igual
   * ao de antes da Fase 28.
   *
   * Na ordem do nome da peça: a leitura devolve na ordem do banco, e o envelope seguinte tem de
   * sair igual ao que subiu, ou a bancada regravaria a obra a cada abertura.
   */
  const nasPecas = (
    deQuem: (pecas: QuantidadesPorPeca) => Readonly<Record<string, number>> | undefined,
  ) => {
    const pieceQuantities = comPecas.flatMap((typology) =>
      Object.entries(deQuem(typology.pieceQuantities!) ?? {})
        .sort(([a], [b]) => a.localeCompare(b, 'pt-BR', { numeric: true }))
        .map(([pieceName, quantity]) => ({ unitTypeId: typology.unitTypeId, pieceName, quantity })),
    );
    return pieceQuantities.length === 0 ? {} : { pieceQuantities };
  };

  /** O número que cada tipologia decidiu para o serviço (fatia 5), já no formato do envelope. */
  const decididoDoServico = (chave: string) =>
    daObra.flatMap((typology) => {
      const decisao = typology.serviceQuantities?.[chave];
      if (decisao === undefined) return [];
      return [
        {
          unitTypeId: typology.unitTypeId,
          ...(decisao.measured === undefined ? {} : { measured: decisao.measured }),
          ...(decisao.reason === undefined ? {} : { reason: decisao.reason }),
          ...(decisao.quantity === undefined ? {} : { quantity: decisao.quantity }),
        },
      ];
    });

  const disciplines = (catalogo?.disciplines ?? []).flatMap((disciplina) => {
    const services = disciplina.services
      // O serviço sem item nenhum ainda sobe quando alguma tipologia deu número a ele: é
      // o serviço por unidade, que o desenho não mede e que só tem o número digitado. A
      // quantidade do serviço numa peça importada também é número (Fase 28, RN-388).
      .filter(
        (servico) =>
          !servicoVazio(servico) ||
          decididoDoServico(servico.key).length > 0 ||
          nasPecas((pecas) => pecas.servicos[servico.key]).pieceQuantities !== undefined,
      )
      .map((servico) => ({
        name: servico.name,
        quantityBySheet: servico.quantityBySheet,
        ...(servico.unit === undefined ? {} : { unit: servico.unit }),
        ...(decididoDoServico(servico.key).length === 0
          ? {}
          : { quantities: decididoDoServico(servico.key) }),
        // A quantidade do serviço em cada peça importada, na unidade dele (Fase 28, RN-388).
        ...nasPecas((pecas) => pecas.servicos[servico.key]),
        items: servico.items.map((item) => ({
          description: item.description,
          kind: item.kind,
          kindOrigin: item.kindOrigin,
          quantityBySheet: item.quantityBySheet,
          ...(item.code === undefined ? {} : { code: item.code }),
          ...(item.unit === undefined ? {} : { unit: item.unit }),
          ...(item.payment === undefined ? {} : { payment: item.payment }),
          ...(item.jobRoleId === undefined ? {} : { jobRoleId: item.jobRoleId }),
          // O índice e a perda são do cadastro do item (Fase 24, fatia 6).
          ...(item.consumptionIndex === undefined
            ? {}
            : { consumptionIndex: item.consumptionIndex }),
          // De onde veio o índice e a foto da média (Fase 40, RN-520).
          ...(item.indexOrigin === undefined ? {} : { indexOrigin: item.indexOrigin }),
          ...(item.indexAverage === undefined ? {} : { indexAverage: item.indexAverage }),
          ...(item.lossPercent === undefined ? {} : { lossPercent: item.lossPercent }),
          // A bitola do aço, só no CORTE E DOBRA (Fase 27, RN-366).
          ...(item.steelGauge === undefined ? {} : { steelGauge: item.steelGauge }),
          quantities: daObra.flatMap((typology) => {
            const chave = itemQuantityKey(servico.key, item.key);
            const quantity = typology.itemQuantities?.[chave];
            if (quantity === undefined) return [];
            // O motivo de discordar do calculado vai junto do número (D9).
            const reason = typology.itemReasons?.[chave];
            return [
              {
                unitTypeId: typology.unitTypeId,
                quantity,
                ...(reason === undefined ? {} : { reason }),
              },
            ];
          }),
          // "B24 leva 33,18 kg" — a quantidade pronta do item em cada peça (Fase 28, RN-384).
          ...nasPecas((pecas) => pecas.itens[itemQuantityKey(servico.key, item.key)]),
        })),
      }));
    // As taxas de aço vão com a disciplina (Fase 27, ADR-176) — e a disciplina vai mesmo sem
    // serviço com item, quando tem taxa a dizer. Sem o campo, o servidor não mexe nelas; a
    // lista vazia é quem as apaga, e é por isso que ela sobe como veio.
    const taxas =
      disciplina.steelRates === undefined
        ? {}
        : {
            steelRates: disciplina.steelRates.map((taxa) => ({
              gauge: taxa.gauge,
              kgPerM3: taxa.kgPerM3,
              // A origem e a foto da média (Fase 40, etapa 3 — RN-533).
              ...(taxa.origin === undefined ? {} : { origin: taxa.origin }),
              ...(taxa.average === undefined ? {} : { average: taxa.average }),
            })),
          };
    // As listas por elemento (Fase 42, RN-573), com a mesma regra: presentes substituem todas as
    // do servidor, e a lista vazia de elementos é quem apaga a última.
    const porElemento =
      disciplina.elementSteelRates === undefined
        ? {}
        : {
            elementSteelRates: disciplina.elementSteelRates.map((lista) => ({
              element: lista.element,
              rates: lista.rates.map((taxa) => ({
                gauge: taxa.gauge,
                kgPerM3: taxa.kgPerM3,
                ...(taxa.origin === undefined ? {} : { origin: taxa.origin }),
                ...(taxa.average === undefined ? {} : { average: taxa.average }),
              })),
            })),
          };
    return services.length === 0 &&
      disciplina.steelRates === undefined &&
      disciplina.elementSteelRates === undefined
      ? []
      : [{ name: disciplina.name, ...taxas, ...porElemento, services }];
  });

  const temPlanilha =
    catalogo !== undefined && (catalogo.fileName !== undefined || catalogo.sources.length > 0);

  return {
    disciplines,
    unitTypes: daObra.map((typology) => ({
      unitTypeId: typology.unitTypeId,
      ...(typology.sheetName === undefined ? {} : { sheetName: typology.sheetName }),
    })),
    // Ausente quando nenhuma tipologia tem o campo: o servidor não mexe em peça nenhuma.
    ...(comPecas.length === 0
      ? {}
      : { pieceQuantityUnitTypes: comPecas.map((typology) => typology.unitTypeId) }),
    ...(temPlanilha
      ? {
          sheetImport: {
            sources: [...catalogo.sources],
            ...(catalogo.fileName === undefined ? {} : { fileName: catalogo.fileName }),
            ...(catalogo.importedAt === undefined ? {} : { importedAt: catalogo.importedAt }),
          },
        }
      : {}),
  };
}

/** O item gravado virando linha do catálogo da tela. A chave é a da RN-251. */
function paraItemDoCatalogo(item: ServiceItem): CatalogItem {
  return {
    key: normalizeName(item.description),
    description: item.description,
    quantityBySheet: item.quantityBySheet,
    kind: item.kind,
    kindOrigin: item.kindOrigin,
    ...(item.purchaseCode === undefined ? {} : { code: item.purchaseCode }),
    ...(item.unit === undefined ? {} : { unit: item.unit }),
    ...(item.payment === undefined ? {} : { payment: item.payment }),
    ...(item.jobRoleId === undefined ? {} : { jobRoleId: item.jobRoleId }),
    ...(item.consumptionIndex === undefined ? {} : { consumptionIndex: item.consumptionIndex }),
    ...(item.indexOrigin === undefined ? {} : { indexOrigin: item.indexOrigin }),
    ...(item.indexAverage === undefined ? {} : { indexAverage: item.indexAverage }),
    ...(item.lossPercent === undefined ? {} : { lossPercent: item.lossPercent }),
    ...(item.steelGauge === undefined ? {} : { steelGauge: item.steelGauge }),
  };
}

/**
 * As taxas de aço da leitura, pela chave da disciplina (Fase 27).
 *
 * `undefined` é o servidor que ainda não conhece as taxas (anterior à `0053`): aí a tela fica
 * com as que tinha, em vez de apagá-las por uma ausência que não quer dizer nada.
 */
function taxasDaLeitura(
  dados: ProjectServiceItems,
): ReadonlyMap<string, TaxasDaDisciplina> | undefined {
  if (dados.steelRates === undefined) return undefined;
  const porDisciplina = new Map<
    string,
    { steelRates?: SteelRate[]; elementSteelRates?: { element: AppliesTo; rates: SteelRate[] }[] }
  >();
  for (const taxa of dados.steelRates) {
    const chave = normalizeName(taxa.disciplineName);
    const taxas = porDisciplina.get(chave) ?? {};
    porDisciplina.set(chave, taxas);
    const linha: SteelRate = {
      gauge: taxa.gauge,
      kgPerM3: taxa.kgPerM3,
      ...(taxa.origin === undefined ? {} : { origin: taxa.origin }),
      ...(taxa.average === undefined ? {} : { average: taxa.average }),
    };
    if (taxa.element === undefined) {
      (taxas.steelRates ??= []).push(linha);
      continue;
    }
    // A linha com elemento é da lista dele, na disciplina que mistura (Fase 42, RN-573).
    const listas = (taxas.elementSteelRates ??= []);
    const lista = listas.find((candidata) => candidata.element === taxa.element);
    if (lista === undefined) listas.push({ element: taxa.element, rates: [linha] });
    else lista.rates.push(linha);
  }
  return porDisciplina;
}

/** As taxas de uma disciplina na leitura: a da disciplina inteira e as por elemento (RN-573). */
type TaxasDaDisciplina = Pick<CatalogDiscipline, 'steelRates' | 'elementSteelRates'>;

/**
 * A disciplina com as taxas que o servidor tem para ela — **ele manda**, como nos itens.
 * A disciplina sem taxa no servidor fica sem o campo, que é "ainda não tem taxa".
 */
function comAsTaxas(
  disciplina: CatalogDiscipline,
  taxas: ReadonlyMap<string, TaxasDaDisciplina> | undefined,
): CatalogDiscipline {
  if (taxas === undefined) return disciplina;
  const { steelRates: _anteriores, elementSteelRates: _porElemento, ...semTaxas } = disciplina;
  const daLeitura = taxas.get(normalizeName(disciplina.name));
  return daLeitura === undefined ? semTaxas : { ...semTaxas, ...daLeitura };
}

/** Os serviços da leitura, pela chave do catálogo da tela. */
function servicosDaLeitura(
  dados: ProjectServiceItems,
): ReadonlyMap<string, ProjectServiceItems['services'][number]> {
  return new Map(
    dados.services.map((servico) => [
      serviceKey(servico.disciplineName, servico.serviceName),
      servico,
    ]),
  );
}

/**
 * O que o servidor tem de itens entrando no catálogo da tela — **ele manda**.
 *
 * Não é merge, ao contrário de `catalogoDoServidor`: desde a fatia 3 da Fase 24 o item é
 * da obra, e o que o navegador guardava dele era cópia. O serviço sem nada gravado fica
 * sem item — é o que ele tem.
 *
 * O serviço que a leitura traz e o catálogo não tem **entra**, com a medida do cadastro
 * (`servicos`, da mesma leitura). Normalmente não acontece — quem chama já passou o
 * catálogo por `catalogoDoServidor` —, mas o nome da disciplina que o catálogo usa vem da
 * estrutura, que é outra leitura e pode estar um passo atrás numa troca de obra. Sem esta
 * entrada, o item do serviço que ficou sem nome sumiria da tela, e a gravação seguinte o
 * tiraria da obra por ausência (RN-255).
 */
export function itensDoServidor(
  catalog: ServiceCatalog,
  dados: ProjectServiceItems,
  servicos: readonly Service[] = [],
): ServiceCatalog {
  const daLeitura = servicosDaLeitura(dados);
  const { fileName: _arquivo, importedAt: _quando, ...semPlanilha } = catalog;

  const comItens = (servico: CatalogService): CatalogService => {
    const gravado = daLeitura.get(servico.key);
    const { unit: _unidade, ...semUnidade } = servico;
    return {
      ...semUnidade,
      items: (gravado?.items ?? []).map(paraItemDoCatalogo),
      quantityBySheet: gravado?.quantityBySheet ?? {},
      ...(gravado?.sheetUnit === undefined ? {} : { unit: gravado.sheetUnit }),
    };
  };

  // O que a leitura tem e o catálogo não, agrupado pela disciplina da própria leitura.
  const conhecidos = new Set(
    catalog.disciplines.flatMap((disciplina) => disciplina.services.map((servico) => servico.key)),
  );
  const cadastroPorId = new Map(servicos.map((service) => [String(service.id), service]));
  const faltando = new Map<string, { nome: string; servicos: CatalogService[] }>();
  for (const [chave, lido] of daLeitura) {
    if (conhecidos.has(chave)) continue;
    const cadastro = cadastroPorId.get(String(lido.serviceId));
    if (cadastro === undefined) continue;
    const disciplina = normalizeName(lido.disciplineName);
    const grupo = faltando.get(disciplina) ?? { nome: lido.disciplineName, servicos: [] };
    grupo.servicos.push(comItens(paraCatalogo(cadastro, lido.disciplineName)));
    faltando.set(disciplina, grupo);
  }

  const taxas = taxasDaLeitura(dados);
  const disciplinas = catalog.disciplines.map((disciplina) => {
    const aMais = faltando.get(disciplina.key);
    faltando.delete(disciplina.key);
    return comAsTaxas(
      {
        ...disciplina,
        services: [...disciplina.services.map(comItens), ...(aMais?.servicos ?? [])],
      },
      taxas,
    );
  });
  const novas = [...faltando.entries()].map(([key, grupo]) =>
    comAsTaxas({ key, name: grupo.nome, services: grupo.servicos }, taxas),
  );

  return {
    ...semPlanilha,
    sources: dados.sheetImport?.sources ?? [],
    ...(dados.sheetImport?.fileName === undefined ? {} : { fileName: dados.sheetImport.fileName }),
    ...(dados.sheetImport?.importedAt === undefined
      ? {}
      : { importedAt: dados.sheetImport.importedAt }),
    disciplines: [...disciplinas, ...novas],
  };
}

/** A aba e o que foi digitado, de uma tipologia — o que a bancada guarda nela. */
export interface LancamentosDaTipologia {
  readonly sheetName?: string;
  readonly itemQuantities: Readonly<Record<string, number>>;
  /** O número de cada serviço, pela chave do serviço (fatia 5). */
  readonly serviceQuantities: Readonly<Record<string, ServiceQuantityDecision>>;
  /** Por que o digitado discorda do calculado, pela chave do item (fatia 6). */
  readonly itemReasons: Readonly<Record<string, string>>;
  /**
   * A quantidade pronta das peças importadas, pelas chaves da tela (Fase 28). Ausente = o
   * servidor não tem peça desta tipologia — e a tela não a põe na gravação das peças.
   */
  readonly pieceQuantities?: QuantidadesPorPeca;
  /** A lembrança das peças renomeadas (20/10/2026, RN-458). Ausente = nenhuma mudou de nome. */
  readonly pieceNameAliases?: Readonly<Record<string, string>>;
}

/**
 * O que cada tipologia decidiu, pela chave que a tela usa (`itemQuantityKey`).
 *
 * O servidor guarda a decisão pelo **id** do item; a bancada, pela chave de texto
 * `serviço » item`. A tradução é a leitura, que diz de que serviço cada item é.
 */
export function lancamentosDoServidor(
  dados: ProjectServiceItems,
): Readonly<Record<string, LancamentosDaTipologia>> {
  const chavePorItem = new Map<string, string>();
  const chavePorServico = new Map<string, string>();
  for (const servico of dados.services) {
    const chaveDoServico = serviceKey(servico.disciplineName, servico.serviceName);
    chavePorServico.set(String(servico.serviceId), chaveDoServico);
    for (const item of servico.items) {
      chavePorItem.set(
        String(item.id),
        itemQuantityKey(chaveDoServico, normalizeName(item.description)),
      );
    }
  }

  return Object.fromEntries(
    dados.unitTypes.map((tipologia) => {
      const itemQuantities: Record<string, number> = {};
      const itemReasons: Record<string, string> = {};
      for (const decisao of tipologia.quantities) {
        const chave = chavePorItem.get(String(decisao.serviceItemId));
        if (chave === undefined) continue;
        itemQuantities[chave] = decisao.quantity;
        if (decisao.reason !== undefined) itemReasons[chave] = decisao.reason;
      }
      // O serviço também pelo id, traduzido para a chave da tela pela mesma leitura.
      const serviceQuantities: Record<string, ServiceQuantityDecision> = {};
      for (const decisao of tipologia.serviceQuantities ?? []) {
        const chave = chavePorServico.get(String(decisao.serviceId));
        if (chave === undefined) continue;
        serviceQuantities[chave] = {
          ...(decisao.measured === undefined ? {} : { measured: decisao.measured }),
          ...(decisao.reason === undefined ? {} : { reason: decisao.reason }),
          ...(decisao.quantity === undefined ? {} : { quantity: decisao.quantity }),
        };
      }
      // As peças importadas pelas mesmas chaves (Fase 28).
      const pieceQuantities = pecasDaLeitura(
        tipologia.pieceQuantities,
        chavePorItem,
        chavePorServico,
      );
      return [
        String(tipologia.unitTypeId),
        {
          itemQuantities,
          itemReasons,
          serviceQuantities,
          ...(tipologia.sheetName === undefined ? {} : { sheetName: tipologia.sheetName }),
          ...(pieceQuantities === undefined ? {} : { pieceQuantities }),
          ...(tipologia.pieceNameAliases === undefined
            ? {}
            : { pieceNameAliases: pieceAliasMap(tipologia.pieceNameAliases) }),
        },
      ];
    }),
  );
}

/**
 * As peças importadas de uma tipologia, da leitura para as chaves da tela (Fase 28).
 *
 * O servidor guarda pelo id — do item, ou do serviço quando o item não vem (a quantidade do
 * serviço na peça, RN-388) —, e a tela pela chave de texto, traduzida pela mesma leitura que
 * traduz o digitado. `undefined` quando nada se traduz: a tipologia fica fora da gravação das
 * peças, em vez de entrar com uma lista vazia que apagaria as do servidor.
 */
function pecasDaLeitura(
  lidas: ProjectServiceItems['unitTypes'][number]['pieceQuantities'],
  chavePorItem: ReadonlyMap<string, string>,
  chavePorServico: ReadonlyMap<string, string>,
): QuantidadesPorPeca | undefined {
  const servicos: Record<string, Record<string, number>> = {};
  const itens: Record<string, Record<string, number>> = {};
  let traduzidas = 0;
  for (const peca of lidas ?? []) {
    const doItem = peca.serviceItemId !== undefined;
    const chave = doItem
      ? chavePorItem.get(String(peca.serviceItemId))
      : chavePorServico.get(String(peca.serviceId));
    if (chave === undefined) continue;
    const destino = doItem ? itens : servicos;
    destino[chave] = { ...destino[chave], [peca.pieceName]: peca.quantity };
    traduzidas += 1;
  }
  return traduzidas === 0 ? undefined : { servicos, itens };
}

/** O servidor já tem alguma coisa desta metade para a obra? */
export function temItensNoServidor(dados: ProjectServiceItems): boolean {
  return (
    dados.services.length > 0 ||
    dados.sheetImport !== undefined ||
    dados.unitTypes.length > 0 ||
    // A taxa de aço também é desta metade (Fase 27): a obra que só tem taxa já é do servidor.
    (dados.steelRates?.length ?? 0) > 0
  );
}

/**
 * O rascunho tem alguma coisa desta metade que valha levar para o servidor?
 *
 * É a pergunta da migração única: quem importou a planilha antes desta fatia tem os itens,
 * a aba de cada tipologia e o que digitou só no navegador. Serviço sem item não conta — ele
 * já é da obra desde a fatia 4 da Fase 14.
 */
export function temItensNoRascunho(rascunho: WorkspaceDraft): boolean {
  const doCatalogo =
    rascunho.catalog !== undefined &&
    (rascunho.catalog.sources.length > 0 ||
      rascunho.catalog.disciplines.some((disciplina) =>
        disciplina.services.some((servico) => !servicoVazio(servico)),
      ));
  const dasTipologias = rascunho.typologies.some(
    (typology) =>
      typology.sheetName !== undefined ||
      Object.keys(typology.itemQuantities ?? {}).length > 0 ||
      Object.keys(typology.serviceQuantities ?? {}).length > 0 ||
      // A peça importada também (Fase 28): é quantidade que só o navegador tem.
      Object.keys(typology.pieceQuantities?.itens ?? {}).length > 0 ||
      Object.keys(typology.pieceQuantities?.servicos ?? {}).length > 0,
  );
  return doCatalogo || dasTipologias;
}

/**
 * O que vai para o `localStorage` depois que o servidor passou a ser o dono dos itens.
 *
 * Sai o catálogo inteiro — a metade de cima já era do servidor, e a de baixo passou a ser —
 * e, de cada tipologia, a aba e o que foi digitado. Guardar uma cópia seria guardar um
 * segundo dono, e o segundo dono é sempre o que fica desatualizado.
 */
export function semOsItens(rascunho: WorkspaceDraft): WorkspaceDraft {
  const { catalog: _catalogo, ...resto } = rascunho;
  return {
    ...resto,
    typologies: rascunho.typologies.map((typology) => {
      const {
        sheetName: _aba,
        itemQuantities: _digitadas,
        itemReasons: _motivos,
        serviceQuantities: _doServico,
        pieceQuantities: _dasPecas,
        ...geometria
      } = typology;
      return geometria;
    }),
  };
}

/** Uma função que pode executar o serviço — o que o select da mão de obra oferece. */
export interface FuncaoDoServico {
  readonly id: JobRoleId;
  readonly name: string;
}

/**
 * As funções da obra, vistas pela tela de itens (RN-252).
 *
 * `doServico` já filtra pela disciplina do serviço (`job_role_disciplines`): o pintor não
 * aparece na alvenaria, e a mão de obra deixa de ser texto livre. `nome` responde pela
 * função que já estava gravada e saiu da disciplina depois — ela continua no item, e a
 * tela precisa dizer qual é.
 *
 * `idDoServico` leva a chave da tela ao id gravado: a sugestão de produtividade (fatia 7)
 * pergunta ao servidor por serviço e função, e o servidor só conhece o id.
 */
export interface FuncoesDaObra {
  readonly doServico: (serviceKey: string) => readonly FuncaoDoServico[];
  readonly nome: (jobRoleId: string) => string | undefined;
  readonly idDoServico: (serviceKey: string) => ServiceId | undefined;
}

/**
 * As funções de cada serviço, pela **disciplina do serviço gravado**.
 *
 * Pelo id, e não pelo nome da disciplina no catálogo da tela: é a mesma pergunta que o
 * servidor faz ao gravar (`job_role_disciplines`), e responder pelo nome ofereceria a
 * função de uma disciplina homônima que ela não executa — e a gravação a recusaria depois.
 * O serviço que ainda não voltou do servidor com id não tem disciplina a perguntar, e não
 * oferece função nenhuma até voltar.
 */
export function funcoesDaObra(
  jobRoles: ProjectServiceItems['jobRoles'],
  servicoPorChave: ReadonlyMap<string, Pick<Service, 'disciplineId'> & { readonly id?: ServiceId }>,
): FuncoesDaObra {
  const nomes = new Map(jobRoles.map((funcao) => [String(funcao.id), funcao.name]));
  return {
    doServico: (chave) => {
      const gravado = servicoPorChave.get(chave);
      if (gravado === undefined) return [];
      return jobRoles
        .filter((funcao) => funcao.disciplineIds.includes(gravado.disciplineId))
        .map((funcao) => ({ id: funcao.id, name: funcao.name }));
    },
    nome: (jobRoleId) => nomes.get(jobRoleId),
    idDoServico: (chave) => servicoPorChave.get(chave)?.id,
  };
}

/**
 * O serviço gravado virando linha de catálogo — sem item: os itens entram depois, por
 * `itensDoServidor`.
 *
 * A chave leva a **disciplina**, como toda chave de serviço (RN-164): o cadastro global
 * tem CONCRETAGEM em vigas, pilares, lajes e blocos, e só o nome normalizado dava a mesma
 * chave às quatro — abrir uma abria todas, e o item acrescentado numa caía nas outras.
 */
function paraCatalogo(service: Service, disciplineName: string): CatalogService {
  return {
    key: serviceKey(disciplineName, service.name),
    name: service.name,
    measureBy: service.measureBy,
    ...(service.appliesTo === undefined ? {} : { appliesTo: service.appliesTo }),
    measureOrigin: 'DECIDIDO',
    items: [],
    quantityBySheet: {},
    ...(service.layerThickness === undefined ? {} : { layerThickness: service.layerThickness }),
    ...(service.sideMargin === undefined ? {} : { sideMargin: service.sideMargin }),
  };
}
